import { Suspense, lazy, useMemo } from "preact/compat";
import { Outlet, RouterProvider, createBrowserRouter } from "react-router";
import Header from "~/components/header.tsx";
import Footer from "~/components/footer.tsx";
import {
	useAuthenticationState,
	isLoadingAuthenticationResult,
} from "~/hooks/auth-state.tsx";
import LoadingSpinner from "~/components/common/loading-spinner.tsx";
import {
	RequireSignedIn,
	RequireSignedOut,
} from "~/features/auth/auth-route-guards.tsx";

const Home = lazy(() => import("~/features/home/home.tsx"));
const CompletedDesigns = lazy(
	() => import("~/features/user/completed-designs.tsx"),
);
const SavedDesigns = lazy(() => import("~/features/user/saved-designs.tsx"));
const Profile = lazy(() => import("~/features/user/profile.tsx"));
const VerifyEmail = lazy(() => import("~/features/user/verify-email.tsx"));

const Login = lazy(() => import("~/features/auth/login.tsx"));
const SignUp = lazy(() => import("~/features/auth/sign-up.tsx"));

const RouterErrorPage = lazy(() => import("~/features/error/router-error.tsx"));

function App() {
	const authResult = useAuthenticationState();

	// The guards resolve auth while rendering, so the route tree no longer depends
	// on auth state and is built once.
	const router = useMemo(
		() =>
			createBrowserRouter([
				{
					element: (
						<Suspense fallback={<LoadingSpinner />}>
							<Outlet />
						</Suspense>
					),
					errorElement: (
						<Suspense fallback={<LoadingSpinner />}>
							<RouterErrorPage />
						</Suspense>
					),
					children: [
						{
							element: (
								<>
									<Header />
									<Outlet />
									<Footer />
								</>
							),
							children: [
								{ index: true, element: <Home /> },

								// Public routes (no auth required)
								{
									path: "verify-email/:token",
									element: <VerifyEmail />,
								},

								// Signed in only
								{
									path: "saved-designs",
									element: (
										<RequireSignedIn>
											<SavedDesigns />
										</RequireSignedIn>
									),
								},
								{
									path: "completed-designs",
									element: (
										<RequireSignedIn>
											<CompletedDesigns />
										</RequireSignedIn>
									),
								},
								{
									path: "profile",
									element: (
										<RequireSignedIn>
											<Profile />
										</RequireSignedIn>
									),
								},

								// Signed out only
								{
									path: "sign-up",
									element: (
										<RequireSignedOut>
											<SignUp />
										</RequireSignedOut>
									),
								},
								{
									path: "login",
									element: (
										<RequireSignedOut>
											<Login />
										</RequireSignedOut>
									),
								},
							],
						},
					],
				},
			]),
		[],
	);

	// Makes a white flash. Also keeps auth settled before any route mounts, which
	// verify-email depends on - it decides whether to auto-login from a ref after
	// an await, and treats "still loading" as skip rather than wait.
	if (isLoadingAuthenticationResult(authResult)) {
		return (
			<div
				style={{
					padding: 30,
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				<LoadingSpinner />
			</div>
		);
	}

	return <RouterProvider router={router} />;
}

export default App;
